import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, RefreshCw } from 'lucide-react';

import { getKeysCapacity } from '@/services/api/custom-targeting';
import type { KeyCapacityItem, KeyCapacityLevel, KeyCapacityResponse } from '@/types/custom-targeting';
import {
    LEVEL_STYLES,
    barWidth,
    buildCapacityCards,
    capacityAlerts,
    formatCompact,
    formatCount,
    formatPercent,
    plural,
} from '@/lib/keyCapacity';
import { formatDateTimeShort, parseApiDate } from '@/lib/apiDate';

const QUERY_KEY = ['custom-targeting-capacity'];

/**
 * Quanto do limite de values do GAM cada key de UTM já usou.
 *
 * Quando a key chega ao limite de values ativos, todo value novo falha no GAM
 * e a receita da campanha deixa de ser atribuída — sem nenhum erro visível no
 * site. Esses cards existem para ver isso chegando.
 */
export default function KeyCapacityCards() {
    const queryClient = useQueryClient();
    const [refreshing, setRefreshing] = useState(false);

    const { data, isLoading, isError } = useQuery({
        queryKey: QUERY_KEY,
        queryFn: () => getKeysCapacity(),
        // O servidor já guarda 15 min; buscar de novo antes disso só devolve o cache.
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    const atualizar = async () => {
        setRefreshing(true);
        try {
            const novo = await getKeysCapacity(true);
            queryClient.setQueryData<KeyCapacityResponse>(QUERY_KEY, novo);
        } finally {
            setRefreshing(false);
        }
    };

    const limite = data?.limit ?? 100000;
    const atencao = data?.thresholds.attention ?? 70;
    const critico = data?.thresholds.critical ?? 90;
    const cards = data ? buildCapacityCards(data.data) : [];
    const alertas = data ? capacityAlerts(data.data) : [];
    const verificado = data?.checked_at ? parseApiDate(data.checked_at) : null;

    return (
        <section className="mb-8">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h2 className="text-base font-semibold text-slate-900">Limite de values no GAM</h2>
                    <p className="mt-0.5 text-sm text-slate-500">
                        Cada key aceita até {formatCount(limite)} values ativos. Os inativos não entram na conta.
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                        <Legenda level="ok" texto={`até ${atencao}%`} />
                        <Legenda level="atencao" texto={`${atencao}–${critico}%`} />
                        <Legenda level="critico" texto={`acima de ${critico}%`} />
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {verificado && (
                        <span
                            className="text-xs text-slate-500"
                            title={data?.cached ? 'Resposta do cache do servidor (15 min)' : 'Consultado agora no GAM'}
                        >
                            Lido no GAM em {formatDateTimeShort(verificado)}
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={atualizar}
                        disabled={refreshing || isLoading}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
                    >
                        <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                        Atualizar
                    </button>
                </div>
            </div>

            {alertas.length > 0 && (
                <div
                    className={`mb-4 flex items-start gap-3 rounded-xl p-4 ring-1 ring-inset ${
                        alertas[0].level === 'critico' ? LEVEL_STYLES.critico.badge : LEVEL_STYLES.atencao.badge
                    }`}
                >
                    <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <div className="text-sm">
                        <p className="font-medium">
                            {alertas.map((a) => `${a.name} em ${formatPercent(a.percent)}`).join(' · ')}
                        </p>
                        <p className="mt-0.5 text-xs opacity-90">
                            Ao chegar em 100% dos values ativos, todo value novo dessas keys falha no GAM e a receita
                            da campanha deixa de ser atribuída.
                        </p>
                    </div>
                </div>
            )}

            {isError && (
                <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
                    Não foi possível ler o limite das keys agora.
                </div>
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                {isLoading &&
                    Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="h-72 animate-pulse rounded-xl border border-slate-200 bg-white" />
                    ))}

                {cards.map((card) => {
                    const estilo = LEVEL_STYLES[card.level];
                    return (
                        <article
                            key={card.name}
                            className={`flex h-full flex-col rounded-xl border bg-white p-4 shadow-sm transition hover:shadow-md ${estilo.border}`}
                        >
                            <header className="flex items-center justify-between gap-2">
                                <div className="flex min-w-0 items-center gap-2">
                                    <span className={`h-2 w-2 flex-shrink-0 rounded-full ${estilo.dot}`} />
                                    <span className="truncate font-mono text-[13px] font-medium text-slate-700">
                                        {card.name}
                                    </span>
                                </div>
                                {card.level !== 'ok' && (
                                    <span
                                        className={`flex-shrink-0 rounded-md px-1.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ${estilo.badge}`}
                                    >
                                        {estilo.label}
                                    </span>
                                )}
                            </header>

                            <div className="mt-4">
                                {card.main ? (
                                    <Uso item={card.main} destaque atencao={atencao} critico={critico} />
                                ) : (
                                    <p className="text-sm text-slate-400">Key não cadastrada</p>
                                )}
                            </div>

                            {card.hour ? (
                                <div className="mt-4 rounded-lg bg-slate-50 px-3 py-2.5">
                                    <p className="mb-1.5 text-[11px] font-medium text-slate-400">Variante por hora</p>
                                    <Uso item={card.hour} atencao={atencao} critico={critico} />
                                </div>
                            ) : (
                                <div className="mt-4 flex min-h-[5.5rem] items-center justify-center rounded-lg border border-dashed border-slate-200 px-3 text-center text-[11px] text-slate-400">
                                    Sem variante por hora
                                </div>
                            )}

                            <div className="flex-1" />

                            {card.main?.base && (
                                <dl
                                    className="mt-4 grid grid-cols-2 gap-2 border-t border-slate-100 pt-3"
                                    title="Contagem da nossa base (ms-custom-targeting), não do GAM"
                                >
                                    <Stat rotulo="Criados" valor={card.main.base.processed} />
                                    <Stat rotulo="Com erro" valor={card.main.base.error} />
                                </dl>
                            )}
                        </article>
                    );
                })}
            </div>
        </section>
    );
}

function Legenda({ level, texto }: { level: KeyCapacityLevel; texto: string }) {
    return (
        <span className="inline-flex items-center gap-1.5">
            <span className={`h-2 w-2 rounded-full ${LEVEL_STYLES[level].dot}`} />
            {texto}
        </span>
    );
}

function Uso({
    item,
    destaque = false,
    atencao,
    critico,
}: {
    item: KeyCapacityItem;
    destaque?: boolean;
    atencao: number;
    critico: number;
}) {
    const estilo = LEVEL_STYLES[item.level];
    const semLeitura = item.gam_active === null || item.gam_active === undefined;
    const largura = barWidth(item.percent);

    return (
        <div title={item.error || undefined}>
            <div className="flex items-baseline justify-between gap-2">
                <span
                    className={
                        destaque
                            ? 'truncate text-2xl font-semibold tracking-tight text-slate-900 tabular-nums'
                            : 'truncate text-base font-semibold text-slate-800 tabular-nums'
                    }
                >
                    {formatCount(item.gam_active)}
                </span>
                <span className={`font-semibold tabular-nums ${destaque ? 'text-sm' : 'text-xs'} ${estilo.text}`}>
                    {formatPercent(item.percent)}
                </span>
            </div>

            <div className={`relative mt-2 w-full rounded-full bg-slate-100 ${destaque ? 'h-2' : 'h-1.5'}`}>
                <div
                    className={`h-full rounded-full transition-all ${estilo.bar}`}
                    // Com valor acima de zero a barra aparece mesmo quando o percentual arredonda para 0.
                    style={{ width: `${largura}%`, minWidth: largura > 0 ? '4px' : undefined }}
                />
                {/* Onde começam atenção e crítico. */}
                <span className="absolute inset-y-0 w-px bg-white" style={{ left: `${atencao}%` }} />
                <span className="absolute inset-y-0 w-px bg-white" style={{ left: `${critico}%` }} />
            </div>

            <div className="mt-1.5 flex items-center justify-between gap-2 text-[11px] text-slate-500">
                {semLeitura ? (
                    <span>Sem leitura do GAM</span>
                ) : (
                    <>
                        <span className="whitespace-nowrap">
                            {destaque ? `ativos de ${formatCompact(item.limit)}` : 'ativos'}
                        </span>
                        {!!item.gam_inactive && (
                            <span className="truncate tabular-nums" title="Inativos não contam no limite">
                                {plural(item.gam_inactive, 'inativo', 'inativos')}
                            </span>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

// Neutro de propósito: os erros da base são, na maioria, values acima de 40
// caracteres — não têm relação com o limite e não merecem o vermelho do alerta.
function Stat({ rotulo, valor }: { rotulo: string; valor: number }) {
    return (
        <div className="min-w-0">
            <dt className="text-[11px] text-slate-400">{rotulo}</dt>
            <dd className="truncate text-sm font-medium text-slate-700 tabular-nums">{formatCount(valor)}</dd>
        </div>
    );
}
